import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type PillProps = HTMLAttributes<HTMLSpanElement> & {
  variant?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger';
};

/**
 * Pill / tag — 50px (full) radius. Used for category chips and lesson
 * status badges. Unselected state per brand uses surface-container-lowest
 * with a "ghost border" (outline-variant @ 15-40% opacity).
 */
export const Pill = forwardRef<HTMLSpanElement, PillProps>(
  ({ className, variant = 'neutral', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 rounded-pill px-3 py-1 text-xs font-semibold uppercase tracking-label',
          variant === 'neutral' && 'bg-surface-container-lowest text-on-surface-variant ghost-border',
          variant === 'primary' && 'bg-primary-fixed text-on-primary-fixed-variant',
          variant === 'success' && 'bg-success-container text-on-success-container',
          variant === 'warning' && 'bg-warning-container text-on-warning-container',
          variant === 'danger' && 'bg-error-container text-on-error-container',
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Pill.displayName = 'Pill';
